import { useStore } from "@/store";

interface AuthorshipLineProps {
  createdBy: string | null | undefined;
  updatedBy?: string | null;
  className?: string;
}

/**
 * "Added by Alice · edited by Bob" for one entity.
 *
 * Reads the two flat fields on the document itself, not the ledger: the
 * current answer is already stored there, so the common case costs no query.
 * The full "who touched this, when" history is the ledger, filtered by
 * `target_path`.
 *
 * Renders nothing without a `createdBy`. Every entity written since the
 * authorship cutover carries one; anything that doesn't is better shown as no
 * attribution than as a confident "Added by another member".
 */
export function AuthorshipLine({
  createdBy,
  updatedBy,
  className,
}: AuthorshipLineProps) {
  const currentUid = useStore((state) => state.currentUser?.uid ?? null);
  const getMemberDisplayName = useStore((state) => state.getMemberDisplayName);

  if (!createdBy) return null;

  const name = (uid: string) =>
    uid === currentUid ? "you" : getMemberDisplayName(uid);

  return (
    <p className={className ?? "text-xs text-muted-foreground"}>
      Added by {name(createdBy)}
      {/* Only worth saying when it was someone else, or a later edit. */}
      {updatedBy && updatedBy !== createdBy && <> · edited by {name(updatedBy)}</>}
    </p>
  );
}
